/**
 * Returns of served lines and payment references (Phase 7B-2, ADR-032).
 *
 * A returned served line is a sales return: the order's total goes down. Whatever of the item was never paid simply
 * stops being owed; whatever was paid for it goes back as money, on the return's authority. Nothing goes back to stock
 * or the kitchen (D6). Returns share the walk-outs' allowance for the location's business day.
 *
 * A payment reference is a string (D5): whitespace removed, leading zeroes kept. Two references are the same when their
 * keys are (case-folded). These mirror `app.line_return_amount`, `app.reference_clean` and `app.reference_key`; the
 * server decides.
 */

/**
 * The value of `qty` more units of a line after `before` were returned. Cumulative, so the returns of a line always
 * sum to exactly its total (an uneven split gives the remainder to the last unit).
 */
export function lineReturnAmount(
  lineTotalMinor: number,
  lineQty: number,
  before: number,
  qty: number,
): number {
  if (lineQty <= 0 || qty <= 0 || before < 0 || before + qty > lineQty) return 0;
  return (
    Math.floor((lineTotalMinor * (before + qty)) / lineQty) -
    Math.floor((lineTotalMinor * before) / lineQty)
  );
}

/** Units of a line still returnable: what was sold less what was returned or refunded line by line. */
export function returnableQty(lineQty: number, returned: number, refunded: number): number {
  return Math.max(0, lineQty - returned - refunded);
}

export interface ReturnSplit {
  /** What the return takes off the order's total. */
  amountMinor: number;
  /** The part of it that was never paid: simply no longer owed. */
  notOwedMinor: number;
  /** The part the customer paid for: given back as money. */
  refundMinor: number;
}

/**
 * How a return of `amountMinor` splits. What was never paid (the total less what was taken, after voids, and what is
 * written off) goes first; money already given back is not owed again here. Mirrors `app.return_assess`.
 */
export function splitReturn(
  amountMinor: number,
  order: { totalMinor: number; paidMinor: number; writtenOffMinor: number },
): ReturnSplit {
  const neverPaid = Math.max(0, order.totalMinor - order.paidMinor - order.writtenOffMinor);
  const refundMinor = Math.max(0, amountMinor - neverPaid);
  return { amountMinor, notOwedMinor: amountMinor - refundMinor, refundMinor };
}

/** What a payment stores: what was keyed, whitespace removed. */
export function cleanReference(reference: string | null | undefined): string | null {
  const s = (reference ?? '').replace(/\s+/g, '');
  return s === '' ? null : s;
}

/** The key two references are compared by (case-folded, no whitespace). */
export function referenceKey(reference: string | null | undefined): string | null {
  const s = cleanReference(reference);
  return s === null ? null : s.toLowerCase();
}

/** An earlier use of a reference, as the server lists it when it asks for a reason. */
export interface ReferenceMatch {
  kind: 'payment' | 'correction' | 'give_back';
  id: string;
  order_id?: string;
  order_number?: string;
  payment_id?: string;
  reversal_id?: string;
  amount_minor?: number;
  method?: string;
  voided?: boolean;
  refunded_minor?: number;
  /** Used at another location of the business: the counter is not shown its details. */
  other_location?: boolean;
  at: string;
}

/** The hint of a capture refused for a reference used before: which tender, and where it was used. */
export interface DuplicateReferenceHint {
  tender_id: string | null;
  reference: string;
  matches: ReferenceMatch[];
}

export function describeMatch(m: ReferenceMatch): string {
  if (m.other_location) return 'used at another location';
  switch (m.kind) {
    case 'payment':
      return `a payment on order ${m.order_number ?? '?'}${m.voided ? ' (voided)' : ''}`;
    case 'correction':
      return 'a payment the owner corrected';
    case 'give_back':
      return 'money given back';
  }
}
